import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { debounce } from 'lodash';
import { CircularProgress, Skeleton, Tooltip } from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { useAppSelector } from '../../redux/app/hooks';
import { useMeetingAttendance } from '../../hooks/useMeetingAttendance';
import { EXPORT_MEETING_ATTENDANCE } from '../LiveClass/api/LiveClassQuery';
import { ExportMeetingAttendanceResponse, MeetingAttendanceStudent } from '../../utils/interface';
import { ATTENDANCE_REPORT_MESSAGES, FETCH_POLICY_NO_CACHE, PATH_ADDRESSES } from '../../utils/constants';
import { formatMeetingDate, formatMeetingTime, formatSecondsAsText } from '../../utils/attendanceDateRange';
import { ATTENDANCE_TAB } from '../../components/LiveClassTabs/LiveClassTabs';
import DataTable, { DataTableColumn } from '../../components/DataTable/DataTable';
import TablePagination from '../../components/TablePagination/TablePagination';
import UserAvatar from '../../components/UserAvatar/UserAvatar';
import CommonSearch from '../../components/Search/CommonSearch';
import SubjectHeader from '../../components/SubjectHeader/SubjectHeader';
import ToasterWithMessage from '../../components/toaster';
import './AttendanceReport.scss';

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;

const isPresent = (status?: string | null) => ['present', 'joined'].includes((status || '').toLowerCase());

// Browsers download the export file from its (signed) URL without leaving the page
const downloadFromUrl = (url: string, fileName?: string | null) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName || '');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const formatStudentDuration = (student: MeetingAttendanceStudent) =>
  student.secondsInMeeting ? formatSecondsAsText(student.secondsInMeeting) : '-';

const getStudentName = (student: MeetingAttendanceStudent) => student.fullName || student.userName || '-';

// The name cell is passed in because its overflow tooltip needs the page's state
const buildColumns = (
  renderName: (student: MeetingAttendanceStudent) => ReactNode,
): DataTableColumn<MeetingAttendanceStudent>[] => [
  {
    key: 'fullName',
    header: 'Name',
    width: '34%',
    className: 'attendanceReportNameCol',
    skeleton: (
      <div className='attendanceReportStudent'>
        <Skeleton variant='circular' width={36} height={36} />
        <Skeleton variant='text' width='55%' />
      </div>
    ),
    render: (student) => (
      <div className='attendanceReportStudent'>
        <UserAvatar name={student.fullName || student.userName} src={student.avatarUrl} size={36} />
        <div className='attendanceReportStudentText'>
          {renderName(student)}
          {/* Phones hide the Duration column and show it here instead */}
          <span className='attendanceReportStudentDuration'>
            {Boolean(student.secondsInMeeting) && (
              <AccessTimeOutlinedIcon className='attendanceReportStudentDurationIcon' aria-hidden='true' />
            )}
            {formatStudentDuration(student)}
          </span>
        </div>
      </div>
    ),
  },
  {
    key: 'secondsInMeeting',
    header: 'Duration In Meeting',
    width: '33%',
    className: 'attendanceReportDurationCol',
    render: formatStudentDuration,
    skeleton: <Skeleton variant='text' width='40%' />,
  },
  {
    key: 'status',
    header: 'Attendance',
    width: '33%',
    className: 'attendanceReportStatusCol',
    skeleton: <Skeleton variant='rounded' width={92} height={26} className='attendanceReportBadgeSkeleton' />,
    render: (student) => (
      <span className={`attendanceReportBadge attendanceReportBadge--${isPresent(student.status) ? 'present' : 'absent'}`}>
        {isPresent(student.status) ? 'Present' : 'Absent'}
      </span>
    ),
  },
];

interface StatCardProps {
  variant: 'rate' | 'duration';
  icon: ReactNode;
  value: ReactNode;
  label: string;
}

const StatCard = ({ variant, icon, value, label }: StatCardProps) => (
  <div className='attendanceReportStat'>
    <div className='attendanceReportStatInner'>
      <span className={`attendanceReportStatIcon attendanceReportStatIcon--${variant}`}>{icon}</span>
      <div className='attendanceReportStatText'>
        <div className='attendanceReportStatValue'>{value}</div>
        <div className='attendanceReportStatLabel'>{label}</div>
      </div>
    </div>
  </div>
);

const AttendanceReport = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  // The live class page's state (subject title, image, colour, tab and date range), passed in when the report was opened
  const liveClassState = location.state?.liveClassState ?? {};
  const { error, success } = ToasterWithMessage();
  const classroomId = useAppSelector((state) => state.auth.selectedClassRoomId);
  const subjectId = localStorage.getItem('selectedSId');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');

  const appliedSearchRef = useRef('');
  // The query runs once typing pauses, from the first page
  const applySearch = useMemo(
    () =>
      debounce((value: string) => {
        // Spaces around the text don't change the search, so they don't re-query, reset the page or scroll
        const nextSearch = value.trim();
        if (nextSearch === appliedSearchRef.current) return;
        appliedSearchRef.current = nextSearch;
        setSearch(nextSearch);
        setPage(1);
      }, SEARCH_DEBOUNCE_MS),
    [],
  );
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const { attendance, loading, error: loadError } = useMeetingAttendance({
    classroomId,
    subjectId,
    sessionId,
    page,
    pageSize,
    search,
  });

  useEffect(() => {
    if (loadError) error(loadError.message || ATTENDANCE_REPORT_MESSAGES.loadFailed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadError]);

  const [exportAttendance, { loading: isExporting }] = useMutation<ExportMeetingAttendanceResponse>(EXPORT_MEETING_ATTENDANCE, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
  });

  const handleExport = async () => {
    if (!classroomId || !subjectId || !sessionId || isExporting) return;
    try {
      const { data } = await exportAttendance({ variables: { cId: classroomId, sjId: subjectId, sessionId } });
      const file = data?.exportMeetingAttendance;
      if (!file?.downloadUrl) {
        error(ATTENDANCE_REPORT_MESSAGES.exportFailed);
        return;
      }
      downloadFromUrl(file.downloadUrl, file.fileName);
      success(ATTENDANCE_REPORT_MESSAGES.exportReady);
    } catch (err) {
      error((err as Error)?.message || ATTENDANCE_REPORT_MESSAGES.exportFailed);
    }
  };

  // Full name in a tooltip only when it is cut off, same as the other overflow tooltips in the app
  const nameRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const [overflowingNames, setOverflowingNames] = useState<Record<string, boolean>>({});

  const checkNameOverflow = useCallback((sId: string) => {
    const element = nameRefs.current[sId];
    if (!element) return;
    const isOverflowing = element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
    setOverflowingNames((prev) => (prev[sId] === isOverflowing ? prev : { ...prev, [sId]: isOverflowing }));
  }, []);

  const columns = useMemo(
    () =>
      buildColumns((student) => (
        <Tooltip title={getStudentName(student)} placement='bottom-start' disableInteractive disableHoverListener={!overflowingNames[student.sId]}>
          <span
            ref={(element) => {
              nameRefs.current[student.sId] = element;
            }}
            className='attendanceReportStudentName'
            onMouseEnter={() => checkNameOverflow(student.sId)}
            onMouseMove={() => checkNameOverflow(student.sId)}
          >
            {getStudentName(student)}
          </span>
        </Tooltip>
      )),
    [overflowingNames, checkNameOverflow],
  );

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(1);
  };

  const isFirstLoad = loading && !attendance;
  const students = attendance?.students?.nodes ?? [];
  const totalCount = attendance?.students?.totalCount ?? 0;
  const meetingTime =
    attendance?.startTime && attendance?.endTime
      ? `${formatMeetingDate(attendance.startTime)} · ${formatMeetingTime(attendance.startTime, attendance.endTime)}`
      : '';

  return (
    <div className='attendanceReport'>
      {/* Same subject header as the live class page (on phones it also has the menu button); back returns to the Attendance tab */}
      <SubjectHeader
        headerName={liveClassState.title}
        headerImage={liveClassState.imageSrc}
        backgroundColor={liveClassState.backGroundColor}
        backNavigation={{
          path: PATH_ADDRESSES.TEACHER_LIVECLASS_PATH,
          state: { ...liveClassState, liveClassTab: ATTENDANCE_TAB },
        }}
      />
      <div className='attendanceReportBody'>
        <div className='attendanceReportHeading'>
          <h1 className='attendanceReportTitle'>{ATTENDANCE_REPORT_MESSAGES.title}</h1>
          <p className='attendanceReportSubtitle'>
            {isFirstLoad ? (
              <Skeleton variant='text' width={280} className='attendanceReportSkeletonText' />
            ) : (
              [attendance?.topic, meetingTime].filter(Boolean).join(' · ')
            )}
          </p>
        </div>

        <div className='attendanceReportStats'>
          <StatCard
            variant='rate'
            icon={<GroupsOutlinedIcon />}
            label='Attendance Rate'
            value={
              isFirstLoad ? (
                <Skeleton variant='text' width={64} />
              ) : (
                <>
                  {attendance?.presentCount ?? 0}
                  <span className='attendanceReportStatTotal'> / {attendance?.rosterCount ?? 0}</span>
                </>
              )
            }
          />
          <StatCard
            variant='duration'
            icon={<VideocamOutlinedIcon />}
            label='Meeting Duration'
            value={isFirstLoad ? <Skeleton variant='text' width={110} /> : formatSecondsAsText(attendance?.meetingSeconds)}
          />
        </div>

        <div className='attendanceReportTableCard'>
          <div className='attendanceReportToolbar'>
            <CommonSearch
              from='AttendanceReport'
              placeholder={ATTENDANCE_REPORT_MESSAGES.searchPlaceholder}
              searchValue={searchText}
              setSearchValue={setSearchText}
              handleSearchDebounce={applySearch}
            />
            <button
              type='button'
              className='attendanceReportExport'
              onClick={handleExport}
              disabled={isExporting || !attendance}
              aria-label='Get Report'
            >
              {isExporting ? <CircularProgress size={16} color='inherit' /> : <FileDownloadOutlinedIcon fontSize='small' />}
              {/* Icon-only on phones */}
              <span className='attendanceReportExportLabel'>{isExporting ? 'Exporting…' : 'Get Report'}</span>
            </button>
          </div>
          <DataTable
            columns={columns}
            rows={students}
            rowKey={(student) => student.sId}
            loading={loading}
            emptyMessage={search ? ATTENDANCE_REPORT_MESSAGES.noMatches : ATTENDANCE_REPORT_MESSAGES.noStudents}
            className='attendanceReportTable'
            skeletonRowCount={pageSize}
            scrollResetKey={`${page}-${pageSize}-${search}`}
          />
          {totalCount > 0 && (
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              disabled={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceReport;
